import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { FasServicesUploadComponent } from 'fas-services';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

import { OskInternalExamSettingsService } from '../../../../api/api-services/osk-internal-exam-settings.service';
import { IOskInternalExamSettingsIm } from '../../../../api/osk-internal-exam-settings-im.model';
import { IOskInternalExamSettingsUm } from '../../../../api/osk-internal-exam-settings-um.model';
import { IOskInternalExamSettingsVm } from '../../../../api/osk-internal-exam-settings-vm.model';
import { IUploadFileResultVm } from '../../../../api/upload-file-result-vm.model';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-internal-exam-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FasServicesUploadComponent,
    ButtonModule,
    CheckboxModule,
    InputNumberModule,
    InputTextModule,
    InputTextareaModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [MessageService],
  templateUrl: './internal-exam-settings.component.html',
  styleUrl: './internal-exam-settings.component.scss',
})
export class InternalExamSettingsComponent implements OnInit {
  private readonly _fb = inject(FormBuilder);
  private readonly _http = inject(HttpClient);
  private readonly _messageService = inject(MessageService);
  private readonly _oskInternalExamSettingsService = inject(OskInternalExamSettingsService);

  public loading = false;
  public saving = false;
  public logoUrl: string | null = null;
  public selectedLogoFile: File | null = null;
  public settings: IOskInternalExamSettingsVm | null = null;

  /** The upload widget replaces the current logo preview instead of showing a second one. */
  public editingLogo = false;
  /** Bumped whenever the stored logo changes, used to bust the browser image cache. */
  public imageVersion = 0;
  public readonly webApiUrl = environment.webApiUrl;

  public readonly internalExamSettingsForm = this._fb.nonNullable.group({
    header: ['', [Validators.required, Validators.maxLength(100)]],
    text: ['', [Validators.required, Validators.maxLength(500)]],
    retryLimitEnabled: [false],
    retryHours: [24, [Validators.required, Validators.min(0), Validators.max(999)]],
    retryMinutes: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
  });

  public ngOnInit(): void {
    this.loadSettings();
  }

  public get retryLimitEnabled(): boolean {
    return this.internalExamSettingsForm.controls.retryLimitEnabled.value;
  }

  /** Settings exist on the backend, so every save has to be an update. */
  public get settingsExist(): boolean {
    return this.settings !== null && this.settings.id > 0;
  }

  public get saveButtonLabel(): string {
    return this.settingsExist ? 'Zapisz zmiany' : 'Utwórz ustawienia';
  }

  /**
   * Picking a file only remembers it - the media upload happens together with the settings
   * save, the same way as e-learning thumbnails and question images.
   */
  public handleLogoSelected(files: File[] | File): void {
    const file = this.getSingleFile(files);
    if (!file) {
      return;
    }

    this.selectedLogoFile = file;
    this._messageService.add({
      severity: 'info',
      summary: 'Plik wybrany',
      detail: `Wybrano plik: ${file.name}`,
    });
  }

  public enableLogoEditing(): void {
    this.editingLogo = true;
  }

  public cancelLogoEditing(): void {
    this.editingLogo = false;
    this.selectedLogoFile = null;
  }

  public removeLogo(): void {
    this.logoUrl = null;
    this.selectedLogoFile = null;
    this.editingLogo = false;
    this.internalExamSettingsForm.markAsDirty();
  }

  public async saveInternalExamSettings(): Promise<void> {
    if (this.internalExamSettingsForm.invalid) {
      this.internalExamSettingsForm.markAllAsTouched();
      return;
    }

    const formValue = this.internalExamSettingsForm.getRawValue();

    // Zero would block the student forever, so the limit needs a real interval to be saved.
    if (formValue.retryLimitEnabled && formValue.retryHours === 0 && formValue.retryMinutes === 0) {
      this.showError('Podaj czas do kolejnego podejścia większy niż zero.');
      return;
    }

    this.saving = true;

    try {
      const logoUrl = this.selectedLogoFile
        ? await this.uploadImageToServer(this.selectedLogoFile)
        : this.logoUrl;

      // Already created settings are updated, missing ones are created.
      if (this.settingsExist) {
        const oskInternalExamSettingsUm: IOskInternalExamSettingsUm = {
          id: this.settings!.id,
          header: formValue.header,
          text: formValue.text,
          logoUrl,
          retryLimitEnabled: formValue.retryLimitEnabled,
          retryHours: formValue.retryHours,
          retryMinutes: formValue.retryMinutes,
        };

        this._oskInternalExamSettingsService.put(oskInternalExamSettingsUm).subscribe({
          next: (settings) => {
            this.applySettings(settings);
            this.saving = false;
            this.showSuccess('Ustawienia zostały zapisane.');
          },
          error: (error: HttpErrorResponse) => {
            this.saving = false;
            this.showError(this.readErrorMessage(error, 'Nie udało się zapisać ustawień.'));
          },
        });

        return;
      }

      const oskInternalExamSettingsIm: IOskInternalExamSettingsIm = {
        header: formValue.header,
        text: formValue.text,
        logoUrl,
        retryLimitEnabled: formValue.retryLimitEnabled,
        retryHours: formValue.retryHours,
        retryMinutes: formValue.retryMinutes,
      };

      this._oskInternalExamSettingsService.post(oskInternalExamSettingsIm).subscribe({
        next: (settings) => {
          this.applySettings(settings);
          this.saving = false;
          this.showSuccess('Ustawienia zostały utworzone.');
        },
        error: (error: HttpErrorResponse) => {
          this.saving = false;

          // The settings were created in another tab/session - reload them and let the user update.
          if (error.status === 409) {
            this.loadSettings();
            this.showError('Ustawienia już istnieją, zostały wczytane do edycji.');
            return;
          }

          this.showError(this.readErrorMessage(error, 'Nie udało się utworzyć ustawień.'));
        },
      });
    } catch {
      this.saving = false;
    }
  }

  public resolveImageUrl(imageUrl: string | null): string {
    if (!imageUrl) {
      return '';
    }

    const resolvedUrl = imageUrl.startsWith('http')
      ? imageUrl
      : environment.webApiUrl + imageUrl.replace('/media', '/uploadMedia');

    // Without this the browser can keep serving the previous logo from its cache after a save.
    // The token only changes when new data arrives, so the image is not re-fetched on every
    // change detection run.
    return `${resolvedUrl}${resolvedUrl.includes('?') ? '&' : '?'}v=${this.imageVersion}`;
  }

  private loadSettings(): void {
    this.loading = true;
    this._oskInternalExamSettingsService.get().subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;

        // 404 simply means this OSK has not created its settings yet - the first save is a POST.
        if (error.status === 404) {
          this.settings = null;
          return;
        }

        this.showError(this.readErrorMessage(error, 'Nie udało się wczytać ustawień.'));
      },
    });
  }

  private applySettings(settings: IOskInternalExamSettingsVm): void {
    this.settings = settings;
    this.selectedLogoFile = null;
    this.editingLogo = false;
    this.imageVersion += 1;
    this.logoUrl = settings.logoUrl ?? null;
    this.internalExamSettingsForm.patchValue({
      header: settings.header ?? '',
      text: settings.text ?? '',
      retryLimitEnabled: settings.retryLimitEnabled,
      retryHours: settings.retryHours,
      retryMinutes: settings.retryMinutes,
    });
    this.internalExamSettingsForm.markAsPristine();
  }

  private async uploadImageToServer(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('publicVisible', 'true');

      const response = await firstValueFrom(
        this._http.post<IUploadFileResultVm>(
          `${environment.webApiUrl}/UploadMedia`,
          formData,
        ),
      );

      if (response.success) {
        return response.fileUrl;
      }

      throw new Error('Upload failed');
    } catch (error) {
      this.showError('Nie udało się przesłać logo.');
      throw error;
    }
  }

  private getSingleFile(files: File[] | File): File | null {
    if (Array.isArray(files)) {
      return files[0] ?? null;
    }

    return files ?? null;
  }

  private readErrorMessage(error: HttpErrorResponse, fallbackMessage: string): string {
    return error?.error?.message || error?.error?.Message || fallbackMessage;
  }

  private showSuccess(detail: string): void {
    this._messageService.add({ severity: 'success', summary: 'Sukces', detail });
  }

  private showError(detail: string): void {
    this._messageService.add({ severity: 'error', summary: 'Błąd', detail });
  }
}
